import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const FALLBACK_DOCUMENT_CATEGORIES = [
  { value: 'deed', label: 'Deed' },
  { value: 'deed_of_trust', label: 'Deed of Trust' },
  { value: 'judgment_lien', label: 'Judgment Lien' },
  { value: 'other', label: 'Other' },
];

function buildUrl(path) {
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(buildUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message = data?.detail || data?.error || `Request failed (${response.status})`;
    const error = new Error(message);
    error.data = data;
    throw error;
  }
  return data;
}

function providerLabel(provider) {
  return provider === 'titlesearcher' ? 'TitleSearcher' : 'US Title Search';
}

function statusColors(status) {
  if (status === 'succeeded') return 'border-green-200 bg-green-50 text-green-800';
  if (status === 'failed' || status === 'partial') return 'border-red-200 bg-red-50 text-red-800';
  return 'border-blue-200 bg-blue-50 text-blue-800';
}

function pullStatusLabel(status) {
  if (status === 'queued') return 'Waiting to download';
  if (status === 'downloading') return 'Downloading';
  if (status === 'attached') return 'Attached to case';
  if (status === 'failed') return 'Download failed';
  return 'Available';
}

function recordedDateValue(value) {
  const text = String(value || '').trim();
  const usDate = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (usDate) {
    const [, month, day, year] = usDate;
    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }
  const isoDate = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoDate) {
    const [, year, month, day] = isoDate;
    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }
  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

function fallbackRecordHeaders(provider, rowLength) {
  const headers = provider === 'titlesearcher'
    ? [
        'Date / Book-Page or Instrument',
        'Type',
        'Legal Description',
        'Grantor / Debtor',
        'Grantee / Secured Party',
        'Cross-References',
      ]
    : [
        'Item',
        'Grantor / Grantee',
        'Other Party',
        'Instrument',
        'Date',
        'Class',
        'Book-Page / File #',
      ];
  return Array.from(
    { length: rowLength },
    (_, index) => headers[index] || `Result field ${index + 1}`,
  );
}

function fullRecordDetails(record) {
  if (Array.isArray(record.display_fields) && record.display_fields.length) {
    return record.display_fields.map((field, index) => ({
      label: field.label || `Result field ${index + 1}`,
      value: field.value,
    }));
  }
  const row = Array.isArray(record.row) ? record.row : [];
  const headers = Array.isArray(record.resultHeaders) && record.resultHeaders.length
    ? record.resultHeaders
    : fallbackRecordHeaders(record.provider, row.length);
  return row.map((value, index) => ({
    label: headers[index] || `Result field ${index + 1}`,
    value,
  }));
}

export default function CaseTitleSearchPanel({ caseId, onDocumentCreated }) {
  const [preflight, setPreflight] = useState(null);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState({});
  const [recordCategories, setRecordCategories] = useState({});
  const [error, setError] = useState('');

  const loadPreflight = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const data = await apiFetch(`/firm/cases/${caseId}/title-search/preflight/`);
      setPreflight(data);
      setRun(data.latest_run || null);
      setError('');
      return data;
    } catch (requestError) {
      setError(requestError.message || 'Could not load the title-search plan.');
      return null;
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    loadPreflight();
  }, [loadPreflight]);

  const titleRecords = useMemo(() => {
    const records = new Map();
    (run?.results || []).forEach((searchResult) => {
      (searchResult.records || []).forEach((record) => {
        if (!record.id) return;
        const stableRecordReference = record.source_record_id || (
          record.reference_label
            ? [record.instrument_type, record.reference_label, record.recorded_date]
                .filter(Boolean)
                .join(':')
            : ''
        );
        const recordKey = stableRecordReference
          ? [
              record.provider || searchResult.provider,
              record.state || searchResult.state,
              record.county || searchResult.county,
              stableRecordReference,
            ].join(':')
          : record.id;
        const existing = records.get(recordKey);
        const source = `${searchResult.display_name || record.search_name || 'Debtor'} · ${searchResult.county || record.county} County`;
        if (existing) {
          existing.searchSources = Array.from(new Set([...existing.searchSources, source]));
        } else {
          records.set(recordKey, {
            ...record,
            provider: record.provider || searchResult.provider,
            state: record.state || searchResult.state,
            county: record.county || searchResult.county,
            resultHeaders: searchResult.headers || [],
            searchSources: [source],
          });
        }
      });
    });
    return Array.from(records.values()).sort((left, right) => {
      const dateDifference = recordedDateValue(right.recorded_date)
        - recordedDateValue(left.recorded_date);
      if (dateDifference) return dateDifference;
      return String(left.reference_label || '').localeCompare(
        String(right.reference_label || ''),
        undefined,
        { numeric: true },
      );
    });
  }, [run]);

  const hasActivePulls = titleRecords.some((record) =>
    ['queued', 'downloading'].includes(record.pull_status)
  );

  useEffect(() => {
    setRecordCategories((current) => {
      const next = { ...current };
      titleRecords.forEach((record) => {
        if (!next[record.id]) {
          next[record.id] = record.category || record.suggested_category || 'other';
        }
      });
      return next;
    });
  }, [titleRecords]);

  useEffect(() => {
    const searchActive = ['queued', 'in_progress'].includes(run?.status);
    if (!run?.id || (!searchActive && !hasActivePulls)) return undefined;
    let active = true;
    const timer = window.setInterval(async () => {
      try {
        const nextRun = await apiFetch(`/firm/title-searches/${run.id}/`);
        if (!active) return;
        setRun(nextRun);
        const nextSearchActive = ['queued', 'in_progress'].includes(nextRun.status);
        const nextPullActive = (nextRun.results || []).some((searchResult) =>
          (searchResult.records || []).some((record) =>
            ['queued', 'downloading'].includes(record.pull_status)
          )
        );
        if (!nextSearchActive && !nextPullActive) {
          window.clearInterval(timer);
          const nextPreflight = await loadPreflight({ quiet: true });
          if (onDocumentCreated) onDocumentCreated(nextPreflight);
        }
      } catch (requestError) {
        if (active) setError(requestError.message || 'Could not refresh the title-search status.');
      }
    }, 2000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [hasActivePulls, loadPreflight, onDocumentCreated, run?.id, run?.status]);

  const searchCount = preflight?.plan?.searches?.length || 0;
  const requiredProviders = useMemo(
    () => (preflight?.plan?.required_providers || []).map(providerLabel).join(' and '),
    [preflight]
  );
  const documentCategories = preflight?.document_categories?.length
    ? preflight.document_categories
    : FALLBACK_DOCUMENT_CATEGORIES;

  const start = async () => {
    const confirmed = window.confirm(
      `Run ${searchCount} title search${searchCount === 1 ? '' : 'es'} through ${requiredProviders || 'the title-search sites'}? `
      + 'This may use paid searches or search units on those accounts.'
    );
    if (!confirmed) return;
    setStarting(true);
    setError('');
    try {
      const nextRun = await apiFetch(`/firm/cases/${caseId}/title-search/start/`, {
        method: 'POST',
        body: JSON.stringify({ confirmed: true }),
      });
      setRun(nextRun);
    } catch (requestError) {
      const blockers = requestError.data?.blockers;
      setError(
        Array.isArray(blockers) && blockers.length
          ? blockers.join(' ')
          : requestError.message || 'Could not start the title search.'
      );
    } finally {
      setStarting(false);
    }
  };

  const selectableRecords = titleRecords.filter((record) =>
    record.download_available
    && !['queued', 'downloading', 'attached'].includes(record.pull_status)
  );
  const selectedCount = selectableRecords.filter((record) => selectedRecords[record.id]).length;

  const selectAllAvailable = () => {
    const shouldSelect = selectedCount !== selectableRecords.length;
    const next = { ...selectedRecords };
    selectableRecords.forEach((record) => {
      next[record.id] = shouldSelect;
    });
    setSelectedRecords(next);
  };

  const pullSelected = async () => {
    const selections = selectableRecords
      .filter((record) => selectedRecords[record.id])
      .map((record) => ({
        record_id: record.id,
        category: recordCategories[record.id] || record.suggested_category || 'other',
      }));
    if (!selections.length) return;
    const confirmed = window.confirm(
      `Download and attach ${selections.length} selected land-record document${selections.length === 1 ? '' : 's'}? `
      + 'The title-search sites may charge for image retrieval, and US Title Search must refresh the applicable name/county search to obtain a current document link.'
    );
    if (!confirmed) return;
    setPulling(true);
    setError('');
    try {
      const data = await apiFetch(`/firm/title-searches/${run.id}/documents/pull/`, {
        method: 'POST',
        body: JSON.stringify({ selections }),
      });
      setRun(data.run);
      setSelectedRecords({});
      const pullStillActive = (data.run?.results || []).some((searchResult) =>
        (searchResult.records || []).some((record) =>
          ['queued', 'downloading'].includes(record.pull_status)
        )
      );
      if (!pullStillActive) {
        const nextPreflight = await loadPreflight({ quiet: true });
        if (onDocumentCreated) onDocumentCreated(nextPreflight);
      }
    } catch (requestError) {
      setError(requestError.message || 'Could not start the title-document downloads.');
    } finally {
      setPulling(false);
    }
  };

  const openDocument = async (document) => {
    const popup = window.open('', '_blank');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(buildUrl(document.download_url), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`Could not open the PDF (${response.status}).`);
      const blobUrl = URL.createObjectURL(await response.blob());
      if (popup) popup.location.href = blobUrl;
      else window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (requestError) {
      if (popup) popup.close();
      setError(requestError.message || 'Could not open the PDF.');
    }
  };

  if (loading) {
    return <div className="rounded-lg bg-white p-6 shadow">Loading title-search plan…</div>;
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2">
            <MagnifyingGlassIcon className="h-6 w-6 text-indigo-700" />
            <h3 className="text-lg font-semibold text-gray-900">Debtor Title Searches</h3>
          </div>
          <p className="mt-2 text-sm leading-6 text-gray-600">
            Searches each filing debtor and saved alias in the residential county and every county
            containing real property. Hawkins and Greene use TitleSearcher; other counties use US Title Search.
          </p>
        </div>
        <button
          type="button"
          onClick={start}
          disabled={!preflight?.ready || starting || ['queued', 'in_progress'].includes(run?.status)}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
          {starting ? 'Starting…' : `Run and Save ${searchCount || ''} Search${searchCount === 1 ? '' : 'es'}`}
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {(preflight?.blockers || []).length > 0 && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="flex items-center gap-2 font-semibold">
            <ExclamationTriangleIcon className="h-5 w-5" /> Fix these before searching
          </div>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {preflight.blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-gray-900">Names to search</h4>
          <div className="mt-2 space-y-2">
            {(preflight?.plan?.names || []).map((name) => (
              <div key={name.search_name} className="rounded bg-gray-50 px-3 py-2 text-sm">
                <div className="font-medium text-gray-900">{name.display_name}</div>
                <div className="text-xs text-gray-500">{name.sources.join(' · ')}</div>
              </div>
            ))}
            {!preflight?.plan?.names?.length && <p className="text-sm text-gray-500">No searchable filing-debtor names.</p>}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-gray-900">Counties to search</h4>
          <div className="mt-2 space-y-2">
            {(preflight?.plan?.counties || []).map((county) => (
              <div key={`${county.state}-${county.county}`} className="rounded bg-gray-50 px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-gray-900">{county.county} County, {county.state}</span>
                  <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800">
                    {providerLabel(county.provider)}
                  </span>
                </div>
                <div className="mt-1 text-xs text-gray-500">{county.sources.join(' · ')}</div>
              </div>
            ))}
            {!preflight?.plan?.counties?.length && <p className="text-sm text-gray-500">No searchable counties.</p>}
          </div>
        </div>
      </div>

      {run && (
        <div className={`mt-5 rounded-lg border px-4 py-3 text-sm ${statusColors(run.status)}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold">{run.status_display}</span>
            <span>{new Date(run.created_at).toLocaleString()}</span>
          </div>
          <p className="mt-1">{run.status_message}</p>
          {run.error_message && <p className="mt-1">{run.error_message}</p>}
          {['queued', 'in_progress'].includes(run.status) && (
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-blue-100">
              <div className="h-full w-1/2 animate-pulse rounded-full bg-indigo-600" />
            </div>
          )}
          {(run.results || []).some((item) => item.status === 'failed') && (
            <ul className="mt-3 list-disc space-y-1 pl-5">
              {run.results.filter((item) => item.status === 'failed').map((item, index) => (
                <li key={`${item.state}-${item.county}-${item.search_name}-${index}`}>
                  {item.display_name} in {item.county} County: {item.error}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {run && !['queued', 'in_progress'].includes(run.status) && (
        <div className="mt-5 rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Land-record documents</h4>
              <p className="mt-1 text-sm text-gray-600">
                Newest records appear first. Select the instruments you need, confirm or change the suggested category, and attach them to this case.
              </p>
            </div>
            {selectableRecords.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={selectAllAvailable}
                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {selectedCount === selectableRecords.length ? 'Clear selection' : 'Select all available'}
                </button>
                <button
                  type="button"
                  onClick={pullSelected}
                  disabled={!selectedCount || pulling || hasActivePulls}
                  className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {pulling ? 'Starting…' : `Download and attach (${selectedCount})`}
                </button>
              </div>
            )}
          </div>

          {titleRecords.length > 0 ? (
            <div className="mt-4 space-y-3">
              {titleRecords.map((record) => {
                const busy = ['queued', 'downloading'].includes(record.pull_status);
                const attached = record.pull_status === 'attached';
                const canSelect = record.download_available && !busy && !attached;
                const details = fullRecordDetails(record);
                const categoryLabel = documentCategories.find(
                  (option) => option.value === record.suggested_category
                )?.label || 'Other';
                return (
                  <div key={record.id} className="rounded-md border border-gray-200 bg-gray-50 p-3">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
                      <label className="flex min-w-0 flex-1 items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          checked={Boolean(selectedRecords[record.id]) && canSelect}
                          disabled={!canSelect}
                          onChange={(event) => setSelectedRecords((current) => ({
                            ...current,
                            [record.id]: event.target.checked,
                          }))}
                        />
                        <span className="min-w-0">
                          <span className="block font-medium text-gray-900">
                            {record.instrument_type || 'Land record'}
                            {record.reference_label ? ` — ${record.reference_label}` : ''}
                          </span>
                          <span className="block text-xs leading-5 text-gray-500">
                            {record.county} County, {record.state} · {providerLabel(record.provider)}
                          </span>
                          <span className="block text-xs leading-5 text-gray-500">
                            Found under {record.searchSources.join(' · ')}
                          </span>
                        </span>
                      </label>
                      {details.length > 0 && (
                        <dl className="grid w-full gap-x-4 gap-y-2 rounded-md border border-gray-200 bg-white p-3 text-xs sm:grid-cols-2 lg:w-[34rem]">
                          {details.map((field, index) => (
                            <div key={`${field.label}-${index}`} className="min-w-0">
                              <dt className="font-semibold text-gray-600">{field.label}</dt>
                              <dd className="mt-0.5 break-words text-gray-900">
                                {String(field.value || '').trim() || '—'}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      )}
                      <div className="w-full lg:w-56">
                        <label className="block text-xs font-medium text-gray-600" htmlFor={`category-${record.id}`}>
                          Document category
                        </label>
                        <select
                          id={`category-${record.id}`}
                          value={recordCategories[record.id] || record.category || record.suggested_category || 'other'}
                          disabled={busy || attached || !record.download_available}
                          onChange={(event) => setRecordCategories((current) => ({
                            ...current,
                            [record.id]: event.target.value,
                          }))}
                          className="mt-1 w-full rounded-md border-gray-300 bg-white text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                          {documentCategories.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <div className="mt-1 text-xs text-gray-500">Suggested: {categoryLabel}</div>
                      </div>
                      <div className="w-full lg:w-44 lg:text-right">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          attached
                            ? 'bg-green-100 text-green-800'
                            : record.pull_status === 'failed'
                              ? 'bg-red-100 text-red-800'
                              : busy
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-200 text-gray-700'
                        }`}>
                          {record.download_available ? pullStatusLabel(record.pull_status) : 'No image available'}
                        </span>
                        {attached && record.download_url && (
                          <button
                            type="button"
                            onClick={() => openDocument(record)}
                            className="mt-2 block w-full text-sm font-semibold text-indigo-700 hover:underline lg:text-right"
                          >
                            View attached PDF
                          </button>
                        )}
                        {record.pull_error && (
                          <p className="mt-2 text-xs leading-5 text-red-700">{record.pull_error}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-4 text-sm text-gray-500">
              {run.results?.some((item) => Number(item.record_count || 0) > 0)
                ? 'This older search did not save selectable instrument details. Run a new title search to enable document downloads.'
                : 'No matching land records were returned.'}
            </p>
          )}
        </div>
      )}

      {(preflight?.documents || []).length > 0 && (
        <div className="mt-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <CheckCircleIcon className="h-5 w-5 text-green-600" /> Saved under “Title Search Results” in Documents
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {preflight.documents.map((document) => (
              <button
                key={document.id}
                type="button"
                onClick={() => openDocument(document)}
                className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-100"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                {document.filename}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
